/*!
 * groups.js — group(): one name for several
 * © 2026 Rob Canning — GPLv3
 *
 *     group(agents, [a1, a2, a3])      agents stands for a1, a2, a3
 *     group(agents, a1, a2)            ... the same, written bare
 *     say(agents, rate:*0.9)           each of them, from where each is
 *     level(agents, 0, 8)  hush(agents)  viz(agents, stroke:#fff)  fx(room, [agents])
 *     group(agents)                    who is in it (the console)
 *     group(agents, off)               gone
 *
 * Wherever a name goes -- a cue's uid (say, synth, audio, text, viz, hush,
 * shape ...) or a list of names (level, xfade, hold, gate, seq, bang, fx)
 * -- a group's name stands for its members: the line runs once for each
 * (cueDispatcher expandGroups). A group may hold groups. Groups are this
 * console's: they do not travel over the network.
 */

import { onReset } from "../system/teardown.js";

const groups = new Map();          // name -> [member names]
onReset("project", function forgetGroups() { groups.clear(); });

/** The members of a name, groups opened (a name that is no group is itself). */
export function membersOf(name, seen = new Set()) {
  const n = String(name);
  if (!groups.has(n) || seen.has(n)) return [n];
  seen.add(n);
  return groups.get(n).flatMap(m => membersOf(m, seen));
}

export const isGroup = name => groups.has(String(name));
export const groupNames = () => [...groups.keys()];

/** Names with any groups among them opened: [agents, s1] -> [a1, a2, a3, s1]. */
const openNames = names => [...new Set([].concat(names).flatMap(n => membersOf(n)))];

/**
 * An AST with groups in its names: one AST per member where the group is
 * its uid, or the same AST with its name list opened -- or null when it
 * names no group. (A bus's own name, fx(room ...), is not opened.)
 */
export function expandGroups(ast) {
  if (!ast || ast.type === "cueGroup" || !groups.size) return null;
  // audio's AST keeps its uid outside args
  if (ast.type === "cueAudio") {
    const uid = ast.uid ?? ast.params?.uid;
    if (uid == null || !isGroup(uid)) return null;
    return membersOf(uid).map(m => ({ ...ast, uid: m, params: { ...ast.params, uid: m } }));
  }
  const args = ast.args;
  if (!Array.isArray(args)) return null;
  let changed = false;
  const listed = args.map(a => {
    if (a.type === "names" && Array.isArray(a.value) && a.value.some(isGroup)) { changed = true; return { ...a, value: openNames(a.value) }; }
    if (a.type === "uid" && Array.isArray(a.value) && a.value.some(isGroup)) { changed = true; return { ...a, value: openNames(a.value) }; }
    return a;
  });
  const uidArg = ast.type === "cueFx" ? null : listed.find(a => a.type === "uid" && typeof a.value === "string" && isGroup(a.value));
  if (uidArg) {
    return membersOf(uidArg.value).map(m => ({ ...ast, args: listed.map(a => (a === uidArg ? { ...a, value: m } : a)) }));
  }
  return changed ? [{ ...ast, args: listed }] : null;
}

/** group(...) as a cue. */
export function handleGroupCue(ast) {
  const arg = k => ast?.args?.find(a => a.type === k)?.value;
  const name = String(arg("uid") ?? "");
  const names = (arg("names") || []).map(String);
  if (!name) { console.warn("[group] group(agents, [a1, a2, a3])"); return; }
  if (names.includes("off")) { groups.delete(name); return; }
  if (!names.length) { console.info(`[group] ${name}: ${groups.has(name) ? membersOf(name).join(", ") : "no such group"}`); return; }
  if (names.includes(name)) { console.warn(`[group] ${name} cannot hold itself`); return; }
  groups.set(name, names);
}
